/**
 * Question type fields component.
 *
 * Renders type-specific fields based on the selected question type.
 *
 * @module QuestionTypeFields
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2 } from 'lucide-react';
import { type Question } from './types';

interface QuestionTypeFieldsProps {
  questionType: string | undefined;
  questionData: Partial<Question>;
  formData?: Partial<Question>;
  errors: Record<string, string>;
  handleInputChange: (field: string, value: any) => void;
  handleOptionChange: (index: number, value: string) => void;
  addOption: () => void;
  removeOption: (index: number) => void;
  isEditMode?: boolean;
}

export const QuestionTypeFields = ({
  questionType,
  questionData,
  formData,
  errors,
  handleInputChange,
  handleOptionChange,
  addOption,
  removeOption,
  isEditMode = false,
}: QuestionTypeFieldsProps) => {
  if (!questionType) return null;

  switch (questionType) {
    case 'multiple-choice-single':
    case 'multiple-choice-multi': {
      const options = isEditMode
        ? formData?.options || []
        : questionData.options || [];
      return (
        <div className="space-y-4">
          <div>
            <Label htmlFor="options" className="font-ui">
              Answer Options
            </Label>
            <div className="space-y-2 mt-2">
              {options.map((option, index) => (
                <div key={`option-${index}-${option}`} className="flex items-center gap-2">
                  <Input
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                  />
                  {options.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeOption(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addOption}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                <span className="font-ui">Add Option</span>
              </Button>
            </div>
            {errors.options && (
              <p className="font-ui text-sm text-destructive mt-1">
                {errors.options}
              </p>
            )}
          </div>

          {questionType === 'multiple-choice-multi' && (
            <div className="p-3 bg-primary/10 rounded-lg">
              <p className="font-ui text-sm text-primary font-medium">
                Multiple answers allowed. Students can select multiple options.
              </p>
            </div>
          )}
        </div>
      );
    }

    case 'true-false':
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              True/False Question
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Students must select either True or False as the answer.
            </p>
          </div>
        </div>
      );

    case 'text-short':
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              Short Text Answer
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Students provide a short text answer.
            </p>
          </div>
        </div>
      );

    case 'text-long':
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              Long Text Answer
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Students provide a longer, more detailed text answer.
            </p>
          </div>
        </div>
      );

    case 'fill-in-blank':
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              Fill in the Blank Question
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Use <code className="bg-background px-1 py-0.5 rounded">___</code> (three underscores) to mark blanks in the question text.
              The question should contain exactly 5 blanks. Each blank will be filled by students based on the context.
            </p>
          </div>
          <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
            <p className="font-ui text-sm text-primary font-medium mb-2">
              Example Format:
            </p>
            <pre className="font-ui text-xs text-foreground whitespace-pre-wrap">
{`Anmeldeformular
Helfen Sie [person] und schreiben Sie die fünf fehlenden Informationen in das Formular.

Name:                  Rossi
Wohnort:              ___
Alter:                ___
Beruf:                ___
Adresse:              ___
E-Mail:               ___`}
            </pre>
          </div>
        </div>
      );

    case 'audio-recording': {
      const recordingDuration = isEditMode
        ? formData?.recordingDuration
        : questionData.recordingDuration;
      const preparationTime = isEditMode
        ? formData?.preparationTime
        : questionData.preparationTime;
      const recordingDurationError = isEditMode
        ? errors.recordingDuration
        : undefined;
      const preparationTimeError = isEditMode
        ? errors.preparationTime
        : undefined;
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              Audio Recording Question
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Students record an audio response.
            </p>
          </div>

          <div>
            <Label htmlFor="recordingDuration" className="font-ui">
              Recording Duration (seconds)
            </Label>
            <Input
              id="recordingDuration"
              type="number"
              value={recordingDuration || ''}
              onChange={(e) => {
                const value =
                  e.target.value === ''
                    ? undefined
                    : Number.parseInt(e.target.value) || 0;
                handleInputChange('recordingDuration', value);
              }}
              placeholder="e.g., 90 for 1.5 minutes"
              min="1"
              max="600"
              className="mt-1"
            />
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Maximum recording time allowed (1-600 seconds, recommended: 60-180
              seconds)
            </p>
            {recordingDurationError && (
              <p className="font-ui text-sm text-destructive mt-1">
                {recordingDurationError}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="preparationTime" className="font-ui">
              Preparation Time (seconds)
            </Label>
            <Input
              id="preparationTime"
              type="number"
              value={preparationTime ?? ''}
              onChange={(e) =>
                handleInputChange(
                  'preparationTime',
                  Number.parseInt(e.target.value) || 0,
                )
              }
              placeholder="e.g., 5 for 5 seconds"
              min="0"
              max="60"
              className="mt-1"
            />
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Time given to students to prepare before recording starts (0-60
              seconds, default: 0)
            </p>
            {preparationTimeError && (
              <p className="font-ui text-sm text-destructive mt-1">
                {preparationTimeError}
              </p>
            )}
          </div>
        </div>
      );
    }

    case 'live-conversation': {
      const preparationTime = isEditMode
        ? formData?.preparationTime
        : questionData.preparationTime;
      const preparationTimeError = isEditMode
        ? errors.preparationTime
        : undefined;
      return (
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-ui text-sm text-foreground font-medium">
              Live Conversation Question
            </p>
            <p className="font-ui text-xs text-muted-foreground mt-1">
              An AI-led, time-boxed spoken dialogue. The AI speaks first; both sides are recorded together. No separate recording duration needed.
            </p>
          </div>

          <div>
            <Label htmlFor="preparationTime" className="font-ui">
              Thinking Time (seconds)
            </Label>
            <Input
              id="preparationTime"
              type="number"
              value={preparationTime ?? ''}
              onChange={(e) =>
                handleInputChange(
                  'preparationTime',
                  Number.parseInt(e.target.value) || 0,
                )
              }
              placeholder="e.g., 90"
              min="0"
              max="600"
              className="mt-1"
            />
            <p className="font-ui text-xs text-muted-foreground mt-1">
              Thinking window before the conversation starts (default: 90 seconds).
            </p>
            {preparationTimeError && (
              <p className="font-ui text-sm text-destructive mt-1">
                {preparationTimeError}
              </p>
            )}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};
